/**
 * Responsibility: one era's archive, and whether anything in it has woken up.
 *
 * The move column is the reason this tab exists rather than the bin being a
 * delete. A card dismissed at $150 that now sells for $400 is a different
 * proposition, and nobody is going to re-check a hundred rejects by hand. So
 * the gem price at the moment of binning is kept and the change against it is
 * the first thing shown.
 *
 * Restoring is deliberately one click and free of consequence: boards are
 * derived, so the card simply rejoins wherever it now ranks.
 */
import { useState } from 'react';
import { NameSortHeader } from '../shelf/NameSortHeader';
import { headerNode } from '../shelf/headerNode';
import { nameSortValue } from '../shelf/nameSort';
import type { NameSort } from '../shelf/nameSort';
import { sortableDate, sortableNumber } from '../shelf/sortValues';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import RestoreIcon from '@mui/icons-material/Restore';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { DataTable } from '@kit/components/data';
import type { DataColumn } from '@kit/components/data';
import { ToolbarIconButton } from '@kit/components/buttons';
import { EMPTY } from '@kit/utils';
import type { ArchivedCard } from '../../api/client';
import { money, toNumber } from './money';

type ArchiveTableProps = {
  rows: ArchivedCard[];
  loading?: boolean;
  busy?: boolean;
  onRestore: (row: ArchivedCard) => void;
  /** Desktop cap, measured against the viewport by the page. */
  maxHeight?: number;
};

const UNCAPPED = 100_000;

/** A move this large is worth the operator looking again. */
const NOTABLE_MOVE = 20;

function when(value: string): string {
  return new Date(value).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function MoveCell({ row }: { row: ArchivedCard }) {
  const change = toNumber(row.change_percent);
  if (change === null) {
    return (
      <Typography variant="body2" color="text.disabled">
        {EMPTY}
      </Typography>
    );
  }

  const notable = Math.abs(change) >= NOTABLE_MOVE;
  const colour = change > 0 ? 'success.main' : change < 0 ? 'error.main' : 'text.secondary';
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
      {notable && change > 0 ? <TrendingUpIcon fontSize="small" color="success" /> : null}
      <Typography
        variant="body2"
        color={colour}
        sx={{ fontWeight: notable ? 700 : 500, fontVariantNumeric: 'tabular-nums' }}
      >
        {change > 0 ? '+' : ''}
        {change.toFixed(1)}%
      </Typography>
    </Stack>
  );
}

/**
 * Column widths are measured, not guessed: headings never wrap, so a
 * heading that does not fit its column is clipped. `tests/table-headers.mjs`
 * renders this table in a browser and fails on a clipped one.
 */
export function ArchiveTable({
  rows,
  loading = false,
  busy = false,
  onRestore,
  maxHeight = 640,
}: ArchiveTableProps) {
  // Which of the two name orderings the card column uses.
  const [nameSort, setNameSort] = useState<NameSort>('alphabetical');
  const columns: DataColumn<ArchivedCard>[] = [
    {
      id: 'card',
      sortValue: (row: ArchivedCard) => nameSortValue(`${row.card_name} #${row.card_number}`, nameSort),
      label: headerNode(
        <NameSortHeader label="Card" value={nameSort} onChange={setNameSort} />,
      ),
      render: (row) => (
        <Stack spacing={0} sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {row.card_name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {[row.set_name, row.card_number].filter(Boolean).join(' · ')}
          </Typography>
        </Stack>
      ),
    },
    {
      id: 'pop',
      sortValue: (row: ArchivedCard) => row.pop_psa10,
      label: 'PSA 10s',
      width: 112,
      align: 'right',
      render: (row) => row.pop_psa10,
    },
    {
      id: 'baseline',
      sortValue: (row: ArchivedCard) => sortableNumber(row.baseline_psa10),
      label: 'When binned',
      width: 144,
      align: 'right',
      render: (row) => money(row.baseline_psa10),
    },
    {
      id: 'latest',
      sortValue: (row: ArchivedCard) => sortableNumber(row.latest_psa10),
      label: 'Now',
      width: 110,
      align: 'right',
      render: (row) => money(row.latest_psa10),
    },
    { id: 'move', sortValue: (row: ArchivedCard) => sortableNumber(row.change_percent), label: 'Move', width: 100, align: 'right', render: (row) => <MoveCell row={row} /> },
    {
      id: 'archived',
      sortValue: (row: ArchivedCard) => sortableDate(row.archived_at),
      label: 'Binned',
      width: 110,
      render: (row) => (
        <Typography variant="caption" color="text.secondary">
          {when(row.archived_at)}
        </Typography>
      ),
    },
    {
      id: 'actions',
      label: '',
      width: 60,
      align: 'right',
      render: (row) => (
        <ToolbarIconButton
          aria-label="Put it back on the board"
          onClick={() => onRestore(row)}
          disabled={busy}
        >
          <RestoreIcon fontSize="small" />
        </ToolbarIconButton>
      ),
    },
  ];

  return (
    <Stack spacing={1} sx={{ minHeight: 0, flexGrow: 1 }}>
      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.discovery_key}
        loading={loading}
        emptyMessage="Nothing binned yet. Cards you bin off a board land here, and keep being watched for a price move."
        maxHeight={{ xs: UNCAPPED, md: maxHeight }}
      />
    </Stack>
  );
}
